import { API_GATEWAY_KEY, JOB_MGMT_API_URL } from '../../../user_constant';
import {
  Job,
  ApplicationPayload,
  Caregiver,
  InterestedApplicant,
  CreateJobData,
  OrderResponse,
} from '../types';

export const API_URL = JOB_MGMT_API_URL;
export const API_KEY = API_GATEWAY_KEY;
export { JOB_MGMT_API_URL };

export type JobListParams = {
  cgid?: number;
  status?: string;
  centre?: number;
  order_id?: number;
};

type ApiErrorBody = {
  error?: string;
  message?: string;
  details?: string[];
};

export class ApiError extends Error {
  status: number;
  details: string[];

  constructor(message: string, status: number, details: string[] = []) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
  }
}

const buildUrl = (
  func: string,
  params: Record<string, string | number | undefined> = {}
) => {
  const query = new URLSearchParams({ func });
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      query.set(key, String(value));
    }
  });
  return `${JOB_MGMT_API_URL}?${query.toString()}`;
};

const fetchWithRetry = async (
  url: RequestInfo,
  options?: RequestInit,
  retries = 2,
  delay = 500
): Promise<Response> => {
  try {
    const response = await fetch(url, options);
    if (response.status >= 500 && retries > 0) {
      await new Promise(resolve => setTimeout(resolve, delay));
      return fetchWithRetry(url, options, retries - 1, delay * 2);
    }
    return response;
  } catch (error) {
    if (retries > 0) {
      await new Promise(resolve => setTimeout(resolve, delay));
      return fetchWithRetry(url, options, retries - 1, delay * 2);
    }
    throw error;
  }
};

const request = async <T,>(
  func: string,
  options: RequestInit = {},
  params: Record<string, string | number | undefined> = {}
): Promise<T> => {
  const headers = new Headers(options.headers);
  headers.set('x-api-key', API_KEY);

  if (options.body) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetchWithRetry(buildUrl(func, params), {
    ...options,
    headers,
  });

  if (!response.ok) {
    let errorBody: ApiErrorBody = {};
    try {
      errorBody = await response.json() as ApiErrorBody;
    } catch {
      // Keep the HTTP status when an upstream response is not JSON.
    }

    const message = errorBody.error
      || errorBody.message
      || `Job API request failed with status ${response.status}`;
    throw new ApiError(message, response.status, errorBody.details || []);
  }

  return response.json() as Promise<T>;
};

const firstOrNotFound = <T,>(rows: T[], entity: string): T => {
  if (!Array.isArray(rows) || rows.length === 0) {
    throw new ApiError(`${entity} not found`, 404);
  }
  return rows[0];
};

export const fetchJobs = async (params: JobListParams = {}): Promise<Job[]> => {
  const rows = await request<Job[]>('JobsList', { method: 'GET' }, params);
  return Array.isArray(rows) ? rows : [];
};

export const fetchJobById = async (id: string): Promise<Job> => {
  const rows = await request<Job[]>('JobGetById', { method: 'GET' }, { id });
  return firstOrNotFound(rows, `Job with ID ${id}`);
};

export const fetchCaregiverById = async (id: string): Promise<Caregiver | null> => {
  try {
    const rows = await request<Caregiver[]>('JobCaregiverGetById', { method: 'GET' }, { id });
    return Array.isArray(rows) && rows.length > 0 ? rows[0] : null;
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) {
      return null;
    }
    throw error;
  }
};

export const applyForJob = async (
  jobId: number,
  applicationData: Omit<ApplicationPayload, 'job_id' | 'created_at'>
): Promise<{ insertId: number }> => {
  const caregiverId = applicationData.caregiver_id;
  const payload: ApplicationPayload = {
    ...applicationData,
    caregiver_id: caregiverId === null || caregiverId === '' ? null : Number(caregiverId),
    job_id: jobId,
    created_at: new Date().toISOString(),
  };

  return request<{ insertId: number }>('JobInterestCreate', {
    method: 'POST',
    body: JSON.stringify({ payload }),
  });
};

export const fetchInterestedApplicants = async (jobId: string): Promise<InterestedApplicant[]> => {
  const rows = await request<InterestedApplicant[]>(
    'JobInterestList',
    { method: 'GET' },
    { job_id: jobId }
  );
  return Array.isArray(rows) ? rows : [];
};

export const updateJob = async (
  jobId: number,
  payload: Partial<Job>
): Promise<{ affectedRows: number; changedRows: number }> => request(
  'JobUpdate',
  {
    method: 'PUT',
    body: JSON.stringify({ payload }),
  },
  { id: jobId }
);

export const createJob = async (jobData: CreateJobData): Promise<{ insertId: number }> => request(
  'JobCreate',
  {
    method: 'POST',
    body: JSON.stringify({ payload: jobData }),
  }
);

export const fetchOrderById = async (orderId: string): Promise<OrderResponse> => {
  const rows = await request<OrderResponse[]>(
    'JobOrderGetById',
    { method: 'GET' },
    { id: orderId }
  );
  return firstOrNotFound(rows, `Order ${orderId}`);
};
